/**
 * V2 source path: customer/midas/components/v2/MidasPageV2.tsx
 * V1 source: customer/midas/components/MidasPage.tsx (page scaffold)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 1 + 2A + 2B + 3
 *
 * Phase 3 wires the V2 sidebar (MidasSidebarV2 + MidasSidebarRailV2) into the
 * page shell, replacing the placeholder Phase 1/2 top band. Sidebar layout
 * mirrors the Paper Phase 3 spec at every breakpoint:
 *
 *   Desktop (≥1201): 280px expanded sidebar OR 56px rail (toggle persists in
 *     local component state for now — Phase 0 didn't model this in slice).
 *     Sidebar + chat + drag-handle + dock side-by-side.
 *   Tablet (769–1200): 56px rail = default. Tap chevron → 320px overlay drawer
 *     slides in from left over chat+dock with backdrop scrim (rgba(42,40,36,0.32)).
 *     Rail stays visible behind the scrim. Chat + dock stay in layout — the
 *     drawer floats above them, NOT a layout shift.
 *   Phone (≤768): no rail. Hamburger in top bar opens a full-screen Sidebar
 *     takeover (per Paper Phase 3 — Sidebar — Spec — Phone state B). The chat
 *     becomes hidden; user taps "← Back to chat" to dismiss.
 *
 * Top bar:
 *   - Hamburger (phone only) — opens sidebar takeover
 *   - "Midas" wordmark + machine-context dot/name (when active)
 *   - "+ Ask Midas" / "+ New chat" CTA (Round 4 — moved here from sidebar)
 *
 * Per the Round 11 invariant the page is wrapped in `<div class="midas-v2-root">`
 * so shadcn theme variables apply.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Menu, Plus } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import {
  setMachineContext,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import { setActiveThreadId } from "../../midasPageSlice";
import { TooltipProvider } from "./ui/tooltip";
import MidasArtifactPanelV2 from "./MidasArtifactPanelV2";
import MidasChatAreaV2 from "./MidasChatAreaV2";
import MidasDebugHarnessV2 from "./MidasDebugHarnessV2";
import MidasDragHandleV2 from "./MidasDragHandleV2";
import MidasPhoneArtifactScreenV2 from "./MidasPhoneArtifactScreenV2";
import MidasSidebarRailV2 from "./MidasSidebarRailV2";
import MidasSidebarV2 from "./MidasSidebarV2";
import { useMidasBreakpoint } from "./hooks/useMidasBreakpoint";
import { FleetHistoryTab } from "./MidasFleetHistoryPickerV2";

// ════════════════════════════════════════════════════════════════
// Constants — match Paper Phase 3 — Sidebar — Spec — Tablet
// ════════════════════════════════════════════════════════════════

const SIDEBAR_EXPANDED_WIDTH = 280; // desktop expanded
const SIDEBAR_RAIL_WIDTH = 56; // tablet default + desktop collapsed
const TABLET_DRAWER_WIDTH = 320; // tablet expanded overlay drawer
const SCRIM_OPACITY = 0.32;

// ════════════════════════════════════════════════════════════════
// usePrefersReducedMotion
// ════════════════════════════════════════════════════════════════

const usePrefersReducedMotion = (): boolean => {
  const [pref, setPref] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setPref(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    // Safari < 14 fallback
    if (typeof (mql as any).addListener === "function") {
      (mql as any).addListener(handler);
      return () => (mql as any).removeListener(handler);
    }
    return undefined;
  }, []);
  return pref;
};

// ════════════════════════════════════════════════════════════════
// Top bar
// ════════════════════════════════════════════════════════════════

interface TopBarProps {
  showHamburger: boolean;
  onHamburgerClick: () => void;
  onNewChat: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  showHamburger,
  onHamburgerClick,
  onNewChat,
}) => {
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const machineName = machineContext?.model
    ? machineContext.manufacturerName
      ? `${machineContext.manufacturerName} ${machineContext.model}`.trim()
      : machineContext.model
    : null;

  return (
    <header className="flex h-[60px] flex-shrink-0 items-center gap-2 border-b border-[rgba(44,38,32,0.08)] bg-midas-bone-light px-3 sm:px-4">
      {showHamburger && (
        <button
          type="button"
          onClick={onHamburgerClick}
          aria-label="Open sidebar"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-midas-graphite outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}

      <div className="flex flex-1 items-center justify-center gap-2 truncate">
        {machineName ? (
          <>
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#5BBF8B]"
              style={{ boxShadow: "0 0 4px rgba(91,191,139,0.7)" }}
            />
            <span
              className="truncate font-sora text-[14px] font-medium text-midas-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {machineName}
              {machineContext?.serialNumber && (
                <span
                  className="ml-2 font-mono text-[11px] text-midas-muted-stone"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  · SN {machineContext.serialNumber}
                </span>
              )}
            </span>
          </>
        ) : (
          <span
            className="font-mono text-[11px] uppercase tracking-[0.16em] text-midas-muted-stone"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Platform · General assistant
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onNewChat}
        aria-label="Ask Midas — new chat"
        className="flex h-11 flex-shrink-0 items-center gap-1.5 rounded-md border border-[rgba(44,38,32,0.12)] bg-white px-3 font-sora text-[13px] font-medium text-midas-graphite outline-none transition-colors duration-150 hover:bg-midas-bone-light hover:border-midas-terracotta focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        <Plus className="h-4 w-4 text-midas-terracotta" strokeWidth={2} aria-hidden="true" />
        <span className="hidden sm:inline">Ask Midas</span>
      </button>
    </header>
  );
};

// ════════════════════════════════════════════════════════════════
// MidasPageV2
// ════════════════════════════════════════════════════════════════

const MidasPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isDesktop, isTablet, isPhone } = useMidasBreakpoint();
  const prefersReducedMotion = usePrefersReducedMotion();

  const artifactPanelOpen = useAppSelector(
    (s) => s.midasPage.artifactPanelOpen
  );
  const hasArtifacts = useAppSelector(
    (s) => s.midasPage.artifactTabs.length > 0
  );

  // Sidebar UI state — kept locally because Phase 0 didn't add this to the slice.
  // Desktop: starts expanded. Tablet/phone: starts collapsed (rail/closed).
  const [desktopExpanded, setDesktopExpanded] = useState(true);
  const [tabletDrawerOpen, setTabletDrawerOpen] = useState(false);
  const [phoneSidebarOpen, setPhoneSidebarOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<FleetHistoryTab>("fleet");

  // Reset overlay states when crossing breakpoints
  useEffect(() => {
    if (!isTablet) setTabletDrawerOpen(false);
    if (!isPhone) setPhoneSidebarOpen(false);
  }, [isTablet, isPhone]);

  // ESC key dismisses overlays
  useEffect(() => {
    if (!tabletDrawerOpen && !phoneSidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setTabletDrawerOpen(false);
        setPhoneSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tabletDrawerOpen, phoneSidebarOpen]);

  // Lock body scroll when phone sidebar is open
  useEffect(() => {
    if (!phoneSidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phoneSidebarOpen]);

  // Top-bar "Ask Midas" / "+ New chat" — Round 4 decision: lives in TOP BAR, not sidebar
  const handleNewChat = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
  }, [dispatch]);

  const handleRailExpand = useCallback(() => {
    if (isTablet) {
      setTabletDrawerOpen(true);
    } else if (isDesktop) {
      setDesktopExpanded(true);
    }
  }, [isDesktop, isTablet]);

  const handleRailSelectTab = useCallback((tab: FleetHistoryTab) => {
    setInitialTab(tab);
  }, []);

  const handleSidebarCollapse = useCallback(() => {
    if (isTablet) {
      setTabletDrawerOpen(false);
    } else if (isDesktop) {
      setDesktopExpanded(false);
    }
  }, [isDesktop, isTablet]);

  // Animation timing — TRANSITION = 240ms cubic-bezier(0.4,0,0.2,1) per spec
  const drawerTransition = prefersReducedMotion
    ? "none"
    : "transform 240ms cubic-bezier(0.4,0,0.2,1)";

  // ── Desktop dock visibility (Phase 2 logic preserved verbatim) ──
  const showDesktopDock = isDesktop && (artifactPanelOpen || hasArtifacts);
  const showTabletDock = isTablet && artifactPanelOpen && hasArtifacts;
  const showPhoneTakeover = isPhone && artifactPanelOpen && hasArtifacts;

  return (
    <TooltipProvider delayDuration={200}>
      <MidasDebugHarnessV2 />
      <div className="midas-v2-root flex h-full min-h-screen w-full flex-col overflow-hidden bg-midas-bone">
        <TopBar
          showHamburger={isPhone}
          onHamburgerClick={() => setPhoneSidebarOpen(true)}
          onNewChat={handleNewChat}
        />

        {/* Body: sidebar + chat + drag-handle + dock */}
        <main className="relative flex flex-1 min-h-0">
          {/* ── Desktop sidebar (expanded or collapsed rail) ── */}
          {isDesktop && (
            <div
              className="flex-shrink-0"
              style={{
                width: desktopExpanded
                  ? `${SIDEBAR_EXPANDED_WIDTH}px`
                  : `${SIDEBAR_RAIL_WIDTH}px`,
                transition: prefersReducedMotion
                  ? "none"
                  : "width 240ms cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {desktopExpanded ? (
                <MidasSidebarV2
                  onCollapse={handleSidebarCollapse}
                  initialTab={initialTab}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="desktop"
                  showKeyboardHint={true}
                />
              ) : (
                <MidasSidebarRailV2
                  onExpand={handleRailExpand}
                  onSelectTab={handleRailSelectTab}
                />
              )}
            </div>
          )}

          {/* ── Tablet rail (always visible — overlay drawer floats over) ── */}
          {isTablet && (
            <div className="flex-shrink-0" style={{ width: `${SIDEBAR_RAIL_WIDTH}px` }}>
              <MidasSidebarRailV2
                onExpand={handleRailExpand}
                onSelectTab={handleRailSelectTab}
              />
            </div>
          )}

          {/* ── Chat + dock split (Phase 1 + 2) ── */}
          <div className="flex min-w-0 flex-1">
            <MidasChatAreaV2 prefersReducedMotion={prefersReducedMotion} />
            {(showDesktopDock || showTabletDock) && (
              <>
                <MidasDragHandleV2 />
                <MidasArtifactPanelV2 />
              </>
            )}
          </div>

          {/* ── Tablet overlay drawer + scrim ── */}
          {isTablet && (
            <>
              {/* Backdrop scrim */}
              <div
                aria-hidden={!tabletDrawerOpen}
                onClick={() => setTabletDrawerOpen(false)}
                className="absolute inset-0 z-40"
                style={{
                  backgroundColor: `rgba(42,40,36,${SCRIM_OPACITY})`,
                  opacity: tabletDrawerOpen ? 1 : 0,
                  pointerEvents: tabletDrawerOpen ? "auto" : "none",
                  transition: prefersReducedMotion
                    ? "none"
                    : "opacity 240ms cubic-bezier(0.4,0,0.2,1)",
                  // Don't cover the rail — drawer slides over chat+dock only
                  left: `${SIDEBAR_RAIL_WIDTH}px`,
                }}
              />
              {/* Drawer — slides from left, sits above scrim */}
              <div
                className="absolute top-0 bottom-0 z-50 shadow-[8px_0_32px_rgba(42,40,36,0.18)]"
                style={{
                  left: `${SIDEBAR_RAIL_WIDTH}px`,
                  width: `${TABLET_DRAWER_WIDTH}px`,
                  transform: tabletDrawerOpen
                    ? "translateX(0)"
                    : `translateX(-${TABLET_DRAWER_WIDTH + SIDEBAR_RAIL_WIDTH}px)`,
                  transition: drawerTransition,
                  pointerEvents: tabletDrawerOpen ? "auto" : "none",
                }}
              >
                <MidasSidebarV2
                  onCollapse={() => setTabletDrawerOpen(false)}
                  initialTab={initialTab}
                  onAfterItemClick={() => setTabletDrawerOpen(false)}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="drawer"
                  showKeyboardHint={false}
                />
              </div>
            </>
          )}

          {/* ── Phone full-screen sidebar takeover ── */}
          {isPhone && phoneSidebarOpen && (
            <div
              className={`absolute inset-0 z-50 flex flex-col ${
                prefersReducedMotion ? "" : "animate-in fade-in-0 duration-200"
              }`}
              style={{
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: "env(safe-area-inset-bottom)",
                backgroundColor: "#FBF6EC",
              }}
              role="dialog"
              aria-modal="true"
              aria-label="MIDAS sidebar"
            >
              {/* Phone takeover top bar */}
              <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-[rgba(44,38,32,0.08)] px-3">
                <button
                  type="button"
                  onClick={() => setPhoneSidebarOpen(false)}
                  aria-label="Back to chat"
                  className="flex h-11 items-center gap-1.5 rounded-md px-2 font-sora text-[13px] font-medium text-midas-warm-graphite outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  <span aria-hidden="true">‹</span>
                  Back to chat
                </button>
                <span
                  className="font-sora text-[15px] font-semibold text-midas-graphite"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  Sidebar
                </span>
                <span aria-hidden="true" className="h-11 w-11" />
              </div>
              {/* Sidebar body — same MidasSidebarV2 in drawer variant */}
              <div className="flex min-h-0 flex-1 flex-col">
                <MidasSidebarV2
                  initialTab={initialTab}
                  onAfterItemClick={() => setPhoneSidebarOpen(false)}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="drawer"
                  showKeyboardHint={false}
                />
              </div>
            </div>
          )}
        </main>

        {/* Phone artifact takeover (Phase 2) */}
        {showPhoneTakeover && <MidasPhoneArtifactScreenV2 />}
      </div>
    </TooltipProvider>
  );
};

export default MidasPageV2;
